import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import { HourglassMedium, Warning, ArrowsClockwise } from "@phosphor-icons/react";

export default function MaturityPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming"); // "upcoming" | "matured"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ default_premature_penalty_pct: 1 });
  const [closeAcc, setCloseAcc] = useState(null); // account object being closed
  const [closeForm, setCloseForm] = useState({ closure_type: "mature", penalty_pct: "", penalty_flat: "", note: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    // Fetch a wide window so we can partition client-side
    const { data } = await api.get("/maturity/list?days=365&include_matured=true");
    setRows(data);
    setLoading(false);
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/settings/penalties");
      setSettings(data);
    } catch { /* not super admin */ }
  };
  useEffect(() => { load(); loadSettings(); }, []);

  const canClose = ["super_admin", "branch_admin", "staff"].includes(user.role);

  const upcoming = rows.filter((r) => !r.is_matured);
  const matured = rows.filter((r) => r.is_matured);
  const list = tab === "upcoming" ? upcoming : matured;

  const openClose = (acc, defaultType) => {
    setCloseAcc(acc);
    setCloseForm({
      closure_type: defaultType || (acc.is_matured ? "mature" : "premature"),
      penalty_pct: acc.premature_penalty_pct ?? settings.default_premature_penalty_pct ?? 1,
      penalty_flat: "",
      note: "",
    });
    setErr("");
  };
  const submitClose = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const body = { closure_type: closeForm.closure_type };
      if (closeForm.closure_type === "premature") {
        if (closeForm.penalty_flat !== "" && closeForm.penalty_flat != null) {
          body.penalty_flat = Number(closeForm.penalty_flat);
        } else if (closeForm.penalty_pct !== "" && closeForm.penalty_pct != null) {
          body.penalty_pct = Number(closeForm.penalty_pct);
        }
      }
      if (closeForm.note) body.note = closeForm.note;
      await api.post(`/savings/${closeAcc.id}/close`, body);
      setCloseAcc(null);
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed to close account");
    } finally { setSaving(false); }
  };

  const previewPenalty = () => {
    if (!closeAcc || closeForm.closure_type !== "premature") return 0;
    if (closeForm.penalty_flat !== "" && closeForm.penalty_flat != null) return Number(closeForm.penalty_flat) || 0;
    const pct = Number(closeForm.penalty_pct) || 0;
    return Math.round(((closeAcc.balance || 0) * pct / 100) * 100) / 100;
  };
  const previewPayout = () => {
    if (!closeAcc) return 0;
    return Math.round(((closeAcc.balance || 0) - previewPenalty()) * 100) / 100;
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="maturity-page">
      <PageHeader
        title="Deposit Maturity"
        subtitle="Upcoming maturities and matured accounts pending renewal or payout."
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[
            ["upcoming", `Upcoming (${upcoming.length})`, HourglassMedium],
            ["matured", `Matured (${matured.length})`, Warning],
          ].map(([v, l, Icon]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              data-testid={`maturity-tab-${v}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                tab === v
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              <Icon size={15} weight={tab === v ? "fill" : "regular"} />
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : list.length === 0 ? (
          <EmptyState title={tab === "upcoming" ? "No upcoming maturities" : "No matured accounts pending"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-left px-4 py-3">Maturity</th>
                  <th className="text-right px-4 py-3">Balance</th>
                  <th className="text-right px-4 py-3">Maturity value</th>
                  <th className="text-left px-4 py-3">Alert</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const days = a.maturity_date
                    ? Math.ceil((new Date(a.maturity_date) - new Date()) / (1000 * 3600 * 24))
                    : null;
                  return (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`maturity-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{a.customer_name || "—"}</div>
                        <div className="text-xs text-slate-500">{a.customer_phone || ""}</div>
                      </td>
                      <td className="px-4 py-3"><Badge tone="info">{a.product?.toUpperCase()}</Badge></td>
                      <td className="px-4 py-3 mono text-slate-700">{shortDate(a.maturity_date)}</td>
                      <td className="px-4 py-3 mono text-right">{money(a.balance || 0)}</td>
                      <td className="px-4 py-3 mono text-right text-emerald-700">{money(a.maturity_value || 0)}</td>
                      <td className="px-4 py-3">
                        {a.is_matured ? (
                          <Badge tone="danger">Matured</Badge>
                        ) : days != null && days <= (a.maturity_reminder_days ?? 7) ? (
                          <Badge tone="warning">
                            <span className="text-amber-700"><span className="blink-dot" />{days}d left</span>
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-500 mono">{days}d</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link to={`/savings/${a.id}`} className="text-emerald-700 hover:underline text-sm mr-3" data-testid={`open-savings-${a.id}`}>Open</Link>
                        {canClose && (
                          <button
                            onClick={() => openClose(a)}
                            data-testid={`close-savings-${a.id}`}
                            className="inline-flex items-center gap-1 text-rose-700 text-sm font-medium hover:underline"
                          >
                            <ArrowsClockwise size={14} weight="bold" /> Close / withdraw
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={!!closeAcc} onClose={() => setCloseAcc(null)} title="Close deposit / withdraw" maxWidth="max-w-lg">
        {closeAcc && (
          <form onSubmit={submitClose} className="space-y-4">
            <div className="text-sm text-slate-600">
              <div><span className="text-slate-400">Account:</span> <span className="mono">{closeAcc.product?.toUpperCase()} #{closeAcc.id.slice(0, 8)}</span></div>
              <div><span className="text-slate-400">Customer:</span> {closeAcc.customer_name}</div>
              <div><span className="text-slate-400">Current balance:</span> <span className="mono font-semibold text-emerald-700">{money(closeAcc.balance || 0)}</span></div>
              <div><span className="text-slate-400">Maturity:</span> <span className="mono">{shortDate(closeAcc.maturity_date)}</span></div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Closure type</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["mature", "On maturity", "No penalty"],
                  ["premature", "Pre-mature", "Penalty applies"],
                ].map(([v, l, s]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setCloseForm({ ...closeForm, closure_type: v })}
                    data-testid={`closure-type-${v}`}
                    className={`text-left border rounded-lg px-3 py-2 ${
                      closeForm.closure_type === v ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <div className="text-sm font-semibold">{l}</div>
                    <div className="text-xs text-slate-500">{s}</div>
                  </button>
                ))}
              </div>
            </div>
            {closeForm.closure_type === "premature" && (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Penalty %" type="number" step="0.01" testId="closure-penalty-pct"
                  value={closeForm.penalty_pct} onChange={(e) => setCloseForm({ ...closeForm, penalty_pct: e.target.value, penalty_flat: "" })}
                />
                <Input
                  label="…or flat penalty (₹)" type="number" step="0.01" testId="closure-penalty-flat"
                  value={closeForm.penalty_flat} onChange={(e) => setCloseForm({ ...closeForm, penalty_flat: e.target.value })}
                />
              </div>
            )}
            <Input label="Note (optional)" testId="closure-note"
              value={closeForm.note} onChange={(e) => setCloseForm({ ...closeForm, note: e.target.value })} />

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Balance</span><span className="mono">{money(closeAcc.balance || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Penalty</span><span className="mono text-rose-600">− {money(previewPenalty())}</span></div>
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
                <span>Payout to customer</span>
                <span className="mono text-emerald-700" data-testid="closure-payout-preview">{money(previewPayout())}</span>
              </div>
            </div>

            {err && <div className="text-sm text-rose-600">{String(err)}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={() => setCloseAcc(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="confirm-close-savings">
                {saving ? "Closing…" : "Confirm closure"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
